import { z } from "zod";

export const SLUG_REGEX = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

const slugSchema = z
  .string()
  .min(1)
  .max(80)
  .regex(SLUG_REGEX, "slug ຕ້ອງເປັນຕົວພິມນ້ອຍ a-z, 0-9 ແລະ ຂີດກາງເທົ່ານັ້ນ");

export const RoomDisplayMediaTypeSchema = z.enum(["image", "video"]);
export type RoomDisplayMediaTypeDTO = z.infer<typeof RoomDisplayMediaTypeSchema>;

export const CreateRoomDisplayScreenSchema = z.object({
  slug: slugSchema,
  name: z.string().min(1).max(200),
  isActive: z.boolean().optional().default(true),
});

export const UpdateRoomDisplayScreenSchema = z.object({
  slug: slugSchema.optional(),
  name: z.string().min(1).max(200).optional(),
  isActive: z.boolean().optional(),
});

export const CreateRoomDisplayPosterSchema = z
  .object({
    imageKey: z.string().min(1),
    mediaType: RoomDisplayMediaTypeSchema.optional().default("image"),
    title: z.string().max(200).nullable().optional(),
    durationSec: z.number().int().min(5).max(120).optional().default(10),
    videoDurationSec: z.number().positive().max(30).optional(),
    isActive: z.boolean().optional().default(true),
    startsAt: z.string().nullable().optional(),
    endsAt: z.string().nullable().optional(),
  })
  .superRefine((data, ctx) => {
    if (data.mediaType === "video" && data.videoDurationSec == null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "videoDurationSec is required for video posters",
        path: ["videoDurationSec"],
      });
    }
  });

export const UpdateRoomDisplayPosterSchema = z.object({
  title: z.string().max(200).nullable().optional(),
  durationSec: z.number().int().min(5).max(120).optional(),
  isActive: z.boolean().optional(),
  startsAt: z.string().nullable().optional(),
  endsAt: z.string().nullable().optional(),
});

export const ReorderRoomDisplayPostersSchema = z.object({
  posterIds: z.array(z.string().min(1)).min(1),
});

export const PosterPresignSchema = z.object({
  contentType: z.string().min(1),
  filename: z.string().min(1).optional(),
  sizeBytes: z.coerce.number().int().positive().optional(),
});

export const RoomDisplayVideoUploadSchema = z.object({
  filename: z.string().min(1),
  contentType: z.string().min(1),
  sizeBytes: z.coerce.number().int().positive(),
  videoDurationSec: z.coerce.number().positive().max(30),
});

export const RoomDisplayVideoMultipartPresignPartsSchema = z.object({
  key: z.string().min(1),
  uploadId: z.string().min(1),
  partNumbers: z.array(z.coerce.number().int().min(1).max(10_000)).min(1),
  expiresIn: z.coerce.number().min(60).max(86400).optional(),
});

export const RoomDisplayVideoMultipartCompleteSchema = z.object({
  key: z.string().min(1),
  uploadId: z.string().min(1),
  parts: z.array(
    z.object({
      partNumber: z.coerce.number().int().min(1).max(10_000),
      etag: z.string().min(1),
    }),
  ),
});

export const RoomDisplayVideoMultipartAbortSchema = z.object({
  key: z.string().min(1),
  uploadId: z.string().min(1),
});

export type CreateRoomDisplayScreenDTO = z.infer<
  typeof CreateRoomDisplayScreenSchema
>;
export type UpdateRoomDisplayScreenDTO = z.infer<
  typeof UpdateRoomDisplayScreenSchema
>;
export type CreateRoomDisplayPosterDTO = z.infer<
  typeof CreateRoomDisplayPosterSchema
>;
export type UpdateRoomDisplayPosterDTO = z.infer<
  typeof UpdateRoomDisplayPosterSchema
>;

export type RoomDisplayPosterDTO = {
  id: string;
  screenId: string;
  imageKey: string;
  mediaType: RoomDisplayMediaTypeDTO;
  title: string | null;
  sortOrder: number;
  durationSec: number;
  isActive: boolean;
  startsAt: string | null;
  endsAt: string | null;
  imageUrl: string | null;
  createdAt: string;
  updatedAt: string;
};

export type RoomDisplayScreenListItemDTO = {
  id: string;
  slug: string;
  name: string;
  isActive: boolean;
  posterCount: number;
  createdAt: string;
  updatedAt: string;
};

export type RoomDisplayScreenDetailDTO = {
  id: string;
  slug: string;
  name: string;
  isActive: boolean;
  posters: RoomDisplayPosterDTO[];
  createdAt: string;
  updatedAt: string;
};

export type PublicDisplayAgendaItem = {
  id: string;
  type: "class" | "private" | "room_booking";
  startTime: string;
  endTime: string;
  title: string;
  roomName?: string;
  instructorNames?: string[];
  status: string;
};

export type PublicDisplayPayload = {
  screen: {
    slug: string;
    name: string;
  };
  date: string;
  serverNow: string;
  items: PublicDisplayAgendaItem[];
  posters: Array<{
    id: string;
    url: string;
    mediaType: RoomDisplayMediaTypeDTO;
    durationSec: number;
  }>;
};
